import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';

import clsx from 'clsx';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import * as T from 'fp-ts/Task';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ExternalResourcesList } from './ExternalResourcesList';
import { PrivacyPolicy } from './PrivacyPolicy';
import { PasswordPreferencesPage } from './SubMenuPage';

import ThreeDotsIcon from '../assets/images/threeDots.svg';
import { ErrorInterrupter } from '../components/ErrorInterrupter';
import { BottomMobileMenu } from '../components/menu/BottomMobileMenu';
import PartnerLogo from '../components/PartnerLogo';
import { LoadingFallback } from '../components/progress/LoadingFallback';
import { useIsAssessmentCompleted } from '../hooks/useIsAssessmentCompleted';
import { User } from '../model/domain/User';
import { agreementState } from '../model/state/agreementState';
import { sideMenuForceOpen } from '../model/state/sideMenuState';
import { themeState } from '../model/state/ThemeState';
import { userState } from '../model/state/userState';
import { BSTask } from '../model/types';
import AuthService from '../service/AuthService';
import { analytics } from '../util/analytics/AnalyticsController';
import { debug } from '../util/logger';

const SignIn = lazy(() => import('./SignIn'));
const WelcomeBack = lazy(() => import('./welcomeBack/WelcomeBackPage'));
const Tour = lazy(() => import('./Tour'));
const StudyPage = lazy(() => import('./study/StudyPage'));
const CongratulationPage = lazy(() => import('./congratulation/CongratulationPage'));
const SignUp = lazy(() => import('./SignUp'));
const PasswordReset = lazy(() => import('./PasswordReset'));
const ChangePassword = lazy(() => import('./ChangePassword'));
const UpdatePassword = lazy(() => import('./UpdatePassword'));
const Feedback = lazy(() => import('./Feedback'));
const Settings = lazy(() => import('./Settings'));
const PerformancePage = lazy(() => import('./performance/PerformancePage'));
const LogoutPage = lazy(() => import('./Logout'));
const DeckList = lazy(() => import('./LibraryPage'));
const DeckDetails = lazy(() => import('./DeckDetails'));
const LibraryTour = lazy(() => import('./LibraryTour'));

const excludedPathsFromInitNavigation = ['/signup', '/updatePws', '/pws'];

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useRecoilState(userState);
  const [isLoading, setLoading] = useState(true);
  const isLoggedIn = useMemo(() => O.isSome(user), [user]);
  const setAgreement = useSetRecoilState(agreementState);
  const theme = useRecoilValue(themeState);
  const [forceShowMenu, setForceShowMenu] = useRecoilState(sideMenuForceOpen);
  const isAssessmentCompleted = useIsAssessmentCompleted();

  const signInWithTokenTask = useCallback<() => BSTask<User>>(
    () => pipe(AuthService.signInWithToken()),
    []
  );

  const onSignInError = useCallback(() => {
    if (location.pathname !== '/privacy-policy') {
      navigate('/');
    }
    setUser(O.none);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const onSignInOk = useCallback(
    (u: User): void => {
      setUser(O.some(u));
      if (u.partner.showTermOfUse) {
        setAgreement(u.partner.termOfUseUrl);
      }
      if (location.pathname !== '/privacy-policy') navigate('/welcomeBack');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUser, navigate]
  );

  const signInOnStartup = useCallback(async () => {
    debug(`Signing in on startup`);

    return pipe(
      signInWithTokenTask(),
      T.map(E.fold(onSignInError, onSignInOk)),
      (invokeTask: T.Task<void>) => invokeTask()
    );
  }, [signInWithTokenTask, onSignInError, onSignInOk]);

  const shouldSignInOnStartup = () => {
    debug('location.pathname=', location.pathname);
    let res = false;
    const doNotSignInOnPaths = ['/pws', '/signup', '/resetPws', '/privacy-policy'];
    doNotSignInOnPaths.forEach((path) => (res = res || location.pathname.startsWith(path)));
    debug('res: ', res, location);

    return !res;
  };

  useEffect(() => {
    if (!excludedPathsFromInitNavigation.some((path) => location.pathname.includes(path))) {
      navigate('/');
    }

    (async function foo() {
      setLoading(true);
      if (shouldSignInOnStartup()) {
        await signInOnStartup();
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableUserPerformance = pipe(
    user,
    O.fold(
      () => false,
      (u) => u.partner.enableUserPerformance
    )
  );

  const enableLibrary = pipe(
    user,
    O.fold(
      () => false,
      (u) => u.partner.enableLibrary
    )
  );

  useEffect(() => {
    document.title = 'Blank Slate';
  }, []);

  useEffect(() => {
    const currentUser = O.toUndefined(user);

    if (currentUser) analytics.identifyUser(currentUser);
    else analytics.resetUser();
  }, [user]);

  const appRoutes = useMemo(() => {
    return (
      <>
        <Routes>
          <Route path={'/'} element={<SignIn />} />
          <Route path={'/welcomeBack'} element={<WelcomeBack />} />
          <Route path={'/study'} element={<StudyPage />} />
          <Route path={'/updatePws'} element={<UpdatePassword />} />
          <Route path={'/privacy-policy'} element={<PrivacyPolicy />} />
          <Route path={'/tour'} element={<Tour />} />
          <Route path={'/feedback'} element={<Feedback />} />
          <Route path={'/congratulation'} element={<CongratulationPage />} />
          <Route path={'/preferences'} element={<Settings />} />
          <Route path={'/settings'} element={<PasswordPreferencesPage />} />
          {enableUserPerformance && isAssessmentCompleted && (
            <Route path={'/performance'} element={<PerformancePage />} />
          )}
          {enableLibrary && isAssessmentCompleted ? (
            <Route path={'/library_tour'} element={<LibraryTour />} />
          ) : null}
          {enableLibrary && isAssessmentCompleted && (
            <Route path={'/library'}>
              <Route path={'/library/deck/:deckId'} element={<DeckDetails />} />
              <Route path={'/library/resources'} element={<ExternalResourcesList />} />
              <Route path={'/library'} element={<DeckList />} />
            </Route>
          )}
          <Route path={'/logout'} element={<LogoutPage />} />
          <Route path={'*'} element={<Navigate replace to="/welcomeBack" />} />
        </Routes>
      </>
    );
  }, [isAssessmentCompleted, enableUserPerformance, enableLibrary]);

  const authRoutes = useMemo(
    () => (
      <>
        <Routes>
          <Route path={'/'} element={<SignIn />} />
          <Route path={'/signin'} element={<SignIn />} />
          <Route path={'/signup'} element={<SignUp />} />
          <Route path={'/resetPws'} element={<PasswordReset />} />
          <Route path={'/privacy-policy'} element={<PrivacyPolicy />} />
          <Route path={'/pws'} element={<ChangePassword />} />
        </Routes>
      </>
    ),
    []
  );

  const partnerLogo = pipe(
    user,
    O.fold(
      () => <span />,
      (u: User) => <PartnerLogo partner={u.partner} />
    )
  );

  const showMobileMenu = () => {
    setForceShowMenu(!forceShowMenu);
  };

  return (
    <div
      className={clsx(
        'relative flex h-screen-safe w-full flex-grow flex-col items-stretch justify-between lg:p-0',
        theme === 'light'
          ? 'bg-bgWhite'
          : 'bg-gradient-to-t from-[#536976] to-[#292E49] lg:bg-bgWhite lg:bg-none'
      )}
    >
      {isLoading ? (
        <LoadingFallback />
      ) : (
        <>
          {isLoggedIn && (
            <div className="flex h-[60px] min-h-[60px] flex-row-reverse items-center justify-between px-10 lg:flex-row lg:px-0">
              <div className="h-max">
                <img
                  src={ThreeDotsIcon}
                  alt="mobile menu"
                  className="lg:hidden"
                  onClick={showMobileMenu}
                />
              </div>
              <div className="flex items-center justify-end">{partnerLogo}</div>
            </div>
          )}
          <ErrorInterrupter>
            <div className="flex h-full justify-center overflow-y-auto lg:items-center">
              {isLoggedIn ? appRoutes : authRoutes}
            </div>
          </ErrorInterrupter>
          {isLoggedIn && location.pathname !== '/study' && isAssessmentCompleted ? (
            <BottomMobileMenu />
          ) : null}
        </>
      )}
    </div>
  );
};

export default Layout;
